import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Spin,
  notification,
} from "antd";
import { FaPlus, FaEdit } from "react-icons/fa";
import { apiHandle } from "../../config/apiHandle";

const TypeManagement = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form] = Form.useForm();

  const statusOptions = ["Active", "Inactive"];

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const response = await apiHandle.get('get-types');
      setTypes(response.data.data);
    } catch (error) {
      notification.error({ message: "Failed to fetch types" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddType = async (values) => {
    try {
      await apiHandle.post('add-type', { name: values.name });
      notification.success({ message: 'Type added successfully' });
      fetchTypes();
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      notification.error({
        message: error.response?.data?.message || "Failed to add type",
      });
    }
  };

  const handleEditType = async (values) => {
    try {
      await apiHandle.post(`edit-type/${editingType._id}`, values);
      notification.success({ message: 'Type updated successfully' });
      fetchTypes();
      setIsModalVisible(false);
      setEditingType(null);
    } catch (error) {
      notification.error({
        message: error.response?.data?.message || "Failed to update type",
      });
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          icon={<FaEdit />}
          onClick={() => {
            setEditingType(record);
            form.setFieldsValue(record);
            setIsModalVisible(true);
          }}
        />
      ),
    },
  ];

  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h1 className="text-3xl font-bold text-center mb-4">Type Management</h1>
      <div className="flex justify-end mb-4">
        <Button
          type="primary"
          icon={<FaPlus />}
          onClick={() => setIsModalVisible(true)}
        >
          Add Type
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={types}
        loading={loading}
        rowKey="_id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingType ? "Edit Type" : "Add New Type"}
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingType(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingType ? handleEditType : handleAddType}
        >
          <Form.Item
            name="name"
            label="Type Name"
            rules={[{ required: true, message: "Please enter type name" }]}
          >
            <Input placeholder="Enter type name" />
          </Form.Item>

          {editingType && (
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select placeholder="Select status">
                {statusOptions.map((option) => (
                  <Select.Option key={option} value={option}>
                    {option}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default TypeManagement;
